import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-md p-8 shadow-sm">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-9 w-9 rounded-md bg-blue-600 grid place-items-center text-white font-display font-bold">S</div>
          <span className="font-display font-semibold text-slate-900">Mediva Solutions</span>
        </div>
        {sent ? (
          <div className="text-center space-y-3" data-testid="forgot-confirmation">
            <div className="h-12 w-12 rounded-md bg-emerald-50 text-emerald-600 grid place-items-center mx-auto"><MailCheck className="h-6 w-6" /></div>
            <h2 className="font-display text-xl font-bold text-slate-900">Check your email</h2>
            <p className="text-sm text-slate-500">If that email is registered, a reset link has been sent. The link expires in 1 hour.</p>
            <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline pt-2" data-testid="back-to-signin"><ArrowLeft className="h-4 w-4" /> Back to sign in</Link>
          </div>
        ) : (
          <>
            <h2 className="font-display text-xl font-bold text-slate-900">Reset your password</h2>
            <p className="text-sm text-slate-500 mt-1">Enter your account email and we'll send a reset link.</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email-input" />
              </div>
              {error && <div className="text-sm text-red-600" data-testid="forgot-error">{error}</div>}
              <Button type="submit" className="w-full bg-blue-700 hover:bg-blue-800" disabled={loading} data-testid="forgot-submit-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
              </Button>
            </form>
            <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mt-6" data-testid="back-to-signin"><ArrowLeft className="h-4 w-4" /> Back to sign in</Link>
          </>
        )}
      </div>
    </div>
  );
}
